import { MapUtils } from '../../../utility/map-utils.js';
import { VersionRange } from '../../core/version-range.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';

export class Nutrients extends DataModel {
  static readonly TYPE_ID: string = '2e81352ffbc54996aaa5ef81df4e3e14';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly calories: number,
    public readonly proteins: number,
    public readonly fats: number,
    public readonly saturatedFats: number,
    public readonly carbs: number,
    public readonly sugars: number,
    public readonly salt: number,
  ) {
    super();
  }

  get $typeId() {
    return Nutrients.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, Nutrients.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): Nutrients {
    return new Nutrients(
        MapUtils.get<number>(map, 'calories'),
        MapUtils.get<number>(map, 'proteins'),
        MapUtils.get<number>(map, 'fats'),
        MapUtils.get<number>(map, 'saturatedFats'),
        MapUtils.get<number>(map, 'carbs'),
        MapUtils.get<number>(map, 'sugars'),
        MapUtils.get<number>(map, 'salt'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'calories': this.calories,
      'proteins': this.proteins,
      'fats': this.fats,
      'saturatedFats': this.saturatedFats,
      'carbs': this.carbs,
      'sugars': this.sugars,
      'salt': this.salt,
    };
  }

  clone(): this {
    return new Nutrients(
        this.calories,
        this.proteins,
        this.fats,
        this.saturatedFats,
        this.carbs,
        this.sugars,
        this.salt,
    ) as this;
  }

  isSame(other: Nutrients): boolean {
    if(other === this) {
      return true;
    }

    return other.calories === this.calories &&
        other.proteins === this.proteins &&
        other.fats === this.fats &&
        other.saturatedFats === this.saturatedFats &&
        other.carbs === this.carbs &&
        other.sugars === this.sugars &&
        other.salt === this.salt;
  }
}

export class NutrientsBuilder extends DataModelBuilder<Nutrients> {
  build(context: SerializationContext, map: SerializedData): Nutrients {
    return Nutrients.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(Nutrients.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(Nutrients.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the following parameters were introduced:
    //  - `saturatedFats` of type `double`
    //  - `sugars` of type `double`
    if(version < 2) {
      map['saturatedFats'] ??= 0.0;
      map['sugars'] ??= 0.0;

      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
